import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { SmallDialog } from '../../components/widgets';
import Button from '../../components/atoms/Button';
import Checkbox from '../../components/atoms/Checkbox';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { TRANSLATION_KEY } from '../constants';
import { monitorShape } from '../propShapes';
import css from './AutoPausedDialog.css';

const DIALOG_KEY = `${TRANSLATION_KEY}.AUTO_PAUSED`;

const noop = () => {};

// Tracks what the user unticked rather than what is ticked, so a monitor the poll adds while
// the dialog is open arrives selected and one it drops needs no cleanup.
const AutoPausedDialogContent = ({ monitors, inactivityDays, resuming, onResume, onKeepPaused, translate }) => {
    const [excluded, setExcluded] = useState(() => new Set());
    const selectedIds = monitors.map(m => m.monitor_id).filter(id => !excluded.has(id));
    const allSelected = selectedIds.length === monitors.length;

    const toggle = (monitorId, checked) =>
        setExcluded(prev => {
            const next = new Set(prev);
            if (checked) next.delete(monitorId);
            else next.add(monitorId);
            return next;
        });

    const toggleAll = checked => setExcluded(checked ? new Set() : new Set(monitors.map(m => m.monitor_id)));

    return (
        <div className={css.dialog}>
            <div className={css.iconBadge}>
                <Icon name="player-pause" size={IconSizes.MEDIUM} variant={IconVariants.ACTIVE} interactive={false} />
            </div>
            <div className={css.heading}>
                <Translate id={`${DIALOG_KEY}.TITLE`} />
            </div>
            <div className={css.body}>
                <Translate id={`${DIALOG_KEY}.BODY`} data={{ days: inactivityDays }} />
            </div>
            <div className={css.listHeader}>
                <Checkbox
                    id="auto-paused-all"
                    label={translate(`${DIALOG_KEY}.SELECT_ALL`)}
                    checked={allSelected}
                    indeterminate={!allSelected && selectedIds.length > 0}
                    disabled={resuming}
                    onChange={toggleAll}
                />
                <span className={css.count}>
                    <Translate
                        id={`${DIALOG_KEY}.SELECTED_COUNT`}
                        data={{ count: selectedIds.length, total: monitors.length }}
                    />
                </span>
            </div>
            <div className={css.list}>
                {monitors.map(monitor => (
                    <Checkbox
                        key={monitor.monitor_id}
                        id={`auto-paused-${monitor.monitor_id}`}
                        className={css.row}
                        labelClass={css.rowLabel}
                        label={monitor.title || translate(`${TRANSLATION_KEY}.CARD.UNTITLED`)}
                        checked={!excluded.has(monitor.monitor_id)}
                        disabled={resuming}
                        onChange={checked => toggle(monitor.monitor_id, checked)}
                    />
                ))}
            </div>
            <div className={css.actions}>
                <Button type="secondary" disabled={resuming} onClick={onKeepPaused}>
                    <Translate id={`${DIALOG_KEY}.KEEP_PAUSED`} />
                </Button>
                <Button
                    disabled={resuming || selectedIds.length === 0}
                    showSpinner={resuming}
                    onClick={() => onResume(selectedIds)}
                >
                    <Translate id={`${DIALOG_KEY}.RESUME`} />
                </Button>
            </div>
        </div>
    );
};

AutoPausedDialogContent.propTypes = {
    monitors: PropTypes.arrayOf(monitorShape).isRequired,
    // Null until the first list response, which is also what any auto-paused monitor arrives in.
    inactivityDays: PropTypes.number,
    resuming: PropTypes.bool.isRequired,
    onResume: PropTypes.func.isRequired,
    onKeepPaused: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

// Closing is "not now": the monitors stay flagged and the dialog returns on the next visit.
// Only Resume and Keep paused answer it for good.
const AutoPausedDialog = ({ open, resuming = false, onClose, ...contentProps }) => (
    <SmallDialog
        open={open}
        onClose={resuming ? noop : onClose}
        disableBackdropClick={resuming}
        classes={{ paperScrollBody: css.paper }}
    >
        <AutoPausedDialogContent {...contentProps} resuming={resuming} />
    </SmallDialog>
);

AutoPausedDialog.propTypes = {
    ...AutoPausedDialogContent.propTypes,
    open: PropTypes.bool.isRequired,
    resuming: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
};

export default withLocalize(AutoPausedDialog);
